/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Divider, Form, Row, Space, Switch, Tag, Typography } from 'antd';
import FormCard from './FormCard';

// Misc imports
import { POWERUPS } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useUpdateBotMutation } from '../../store/bots/botsApi';
import useTheme from '../../utils/hooks/useTheme';

// TODO: Refactor to improve codestyle and reduce duplicate code (eg. for form item rendering)
const PowerupsForm = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { tenantId, orgId } = useParams();
  const { formProps } = useTheme();

  const { data: bot, isFetching: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [updateBot, { isLoading: isUpdatingBot, isSuccess: isUpdateSuccess, reset: resetUpdate }] = useUpdateBotMutation();

  const initialValues = useMemo(() => bot.powerups.reduce((acc, curr) => ({ ...acc, [curr]: true }), {}), [bot]);
  const [, updateState] = useState(); // Hook to force a component re-render

  useEffect(() => {
    if (isUpdateSuccess) {
      updateState(form.getFieldsValue(true));
      resetUpdate();
    }
  }, [isUpdateSuccess]);

  const onSubmit = async (values) => {
    const powerups = [POWERUPS.SETTINGS.key];
    for (const formValue of Object.entries(values)) {
      if (formValue[1] === true) {
        powerups.push(formValue[0]);
      }
    }

    const valuesToSubmit = {
      // Always include the SETTINGS powerup (it is the one we're currently using for viewing this form)
      // Also clean up duplicate values
      powerups: [...new Set([...powerups])]
    };

    await updateBot({ orgId, tenantId, data: valuesToSubmit }).unwrap();
  };

  if (!bot) {
    return;
  }

  return (
    <FormCard
      name="powerupsForm"
      layout="horizontal"
      onFinish={onSubmit}
      form={form}
      initialValues={initialValues}
      wrapperCol={{ xs: 24, md: 18, xl: 14, xxl: 12 }}
      onFieldsChange={(_, allFields) => {
        updateState(allFields);
      }}
      footerActions={[
        <Button
          key="submit"
          type="primary"
          size="large"
          disabled={isFetchingBot || isUpdatingBot}
          onClick={() => form.submit()}
        >
          {t('Save Changes')}
        </Button>
      ]}
    >
      <Divider className="mt-0 mb-3 opacity-0" />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text className="fs-base">
            {t('Agent Behavior')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Design and manage your Agent\'s behavior.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Space align="baseline" size="small" direction="vertical">
            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.FLOWS.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.FLOWS.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('Flows')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Design generic flows to guide the end-user.')}
                </Typography.Paragraph>
              </Col>
            </Space>

            <Space align="baseline" size="small" >
              <Form.Item className="mb-0" name={POWERUPS.AUTOMATED_ANSWERS.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.AUTOMATED_ANSWERS.icon}
                </Tag>

                <Typography.Text className="fs-base pt-0">
                  {t('Automated Answers')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Create NLP-powered conversational experiences.')}
                </Typography.Paragraph>
              </Col>
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider className="mt-0 mb-3" />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text className="fs-base">
            {t('Agent Controls')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Further enhance your Agent\'s powers.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Space align="baseline" size="small" direction="vertical">
            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.BACKUPS.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.BACKUPS.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('Backups')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Add Agent backups to easily restore prior versions.')}
                </Typography.Paragraph>
              </Col>
            </Space>

            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.NLP_CACHING.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.NLP_CACHING.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('NLP Caching')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Improve Agent response times by caching the outcome of NLP processing of incoming messages.')}
                </Typography.Paragraph>
              </Col>
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider className="mt-0 mb-3" />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text className="fs-base">
            {t('Agent Records')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Access chat and survey records.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Space align="baseline" size="small" direction="vertical">
            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.CHAT_SESSIONS.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.CHAT_SESSIONS.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('Chat Sessions')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Access and download the chat sessions.')}
                </Typography.Paragraph>
              </Col>
            </Space>

            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.SURVEYS.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.SURVEYS.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('Surveys')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Access and download the survey data.')}
                </Typography.Paragraph>
              </Col>
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider className="mt-0 mb-3" />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text className="fs-base">
            {t('Agent Statistics & Monitoring')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Turn data into actions with powerful analytics tools.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Space align="baseline" size="small" direction="vertical">
            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.ANALYTICS.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.ANALYTICS.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('Analytics')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Get actionable insights from real-time analytics.')}
                </Typography.Paragraph>
              </Col>
            </Space>

            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.MONITORING.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.MONITORING.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('Monitoring')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Monitor your Agent\'s performance.')}
                </Typography.Paragraph>
              </Col>
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider className="mt-0 mb-3" />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text className="fs-base">
            {t('Engagement')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Engage with your audience with broadcasts.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Space align="baseline" size="small" direction="vertical">
            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.BROADCASTS.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.BROADCASTS.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('Broadcasts')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Schedule and send announcements to targeted audiences.')}
                </Typography.Paragraph>
              </Col>
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider className="mt-0 mb-3" />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text className="fs-base">
            {t('Applications')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Create unique experiences with additional applications.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Space align="baseline" size="small" direction="vertical">
            <Space align="baseline" size="small">
              <Form.Item className="mb-0" name={POWERUPS.HRWIZ.key} valuePropName="checked">
                <Switch/>
              </Form.Item>

              <Col className="pl-0">
                <Tag className="border-0 border-radius-base pl-0 pr-2 m-0">
                  {POWERUPS.HRWIZ.icon}
                </Tag>

                <Typography.Text className="fs-base">
                  {t('HRwiz')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Automate HR processes with the Employee Experience app.')}
                </Typography.Paragraph>
              </Col>
            </Space>
          </Space>
        </Col>
      </Row>
    </FormCard>
  );
};

export default PowerupsForm;
